/**
 * The Shortlist's own grouping — which of Upcoming/Undated/Went a saved row
 * falls into, and (Upcoming only) whether it still needs a night picked.
 *
 * Pure and dependency-free for the reason `listSummary.ts` beside it is: the
 * root suite (`test/shortlist.test.ts`) imports this file directly and Node's
 * ESM resolver will not guess the extension a bundler would, so this can only
 * ever import types that are erased entirely — `FeedRow` and `Commitment`
 * from `./types` are `import type`, gone by the time Node runs the file — and
 * two files that are themselves dependency-free and named with their
 * extension: `daylong.ts` (what a festival's "over" is) and `today.ts`
 * (`berlinDay`).
 * `Showing` is declared locally rather than imported from `components/CardBody`
 * for the same reason `browse.ts` and `rowMeta.ts` declare their own: that
 * file is not `import type`-only.
 */
import type { Commitment, FeedRow } from './types'
import { daylongHasEnded } from './daylong.ts'
import { berlinDay } from './today.ts'

/** One showing, structurally — `ShowingLite` satisfies it. */
export interface Showing {
  starts_at: string
  kind?: string | null
}

const MS_DAY = 86_400_000
const MS_WEEK = 7 * MS_DAY

/** Every entry id a card stands for — copied from `entries.ts`'s `idsOf`, the
 *  same copy `listSummary.ts` keeps for the reason above. */
const idsOf = (e: FeedRow): string[] => e.group_ids ?? [e.id]

/** The showing this card's ids carry, flattened — a merged film card can have
 *  several. */
function showingsFor(e: FeedRow, showings: Map<string, Showing[]>): Showing[] {
  return idsOf(e).flatMap(id => showings.get(id) ?? [])
}

/** This card's own row in `commitments`, where one exists. */
function commitmentFor(e: FeedRow, commitments: Commitment[]): Commitment | null {
  const ids = idsOf(e)
  return commitments.find(c => c.entry_id && ids.includes(c.entry_id)) ?? null
}

/**
 * The one moment this row is judged against — a commitment's own time over
 * the feed's next showing over the run's own start, the same order
 * `shortlistSummary` reads a commitment in, because a saved run's earliest
 * showing is not always the one actually in the diary.
 */
function whenOf(e: FeedRow, commitment: Commitment | null): string | null {
  return commitment?.starts_at ?? commitment?.on_date ?? e.next_showing ?? e.run_start ?? null
}

/** A bare calendar day (`run_end`, a commitment's `on_date`) as opposed to an
 *  instant. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

/** Is this moment behind `now`? A bare day is behind it only once that day is
 *  over *in Berlin*. `new Date('2026-10-06')` is midnight UTC, which is 02:00
 *  in Berlin, so comparing it as an instant put the last day of a run, and a
 *  commitment for today with no time, in Went from 02:00 on the day itself. */
function isBehind(when: string, now: Date): boolean {
  if (DATE_ONLY.test(when)) return when < berlinDay(now)
  return new Date(when).getTime() < now.getTime()
}

/** The night Doug pencilled in has gone by. Says nothing about whether the
 *  event has: a film with a screening on the 4th and another on the 6th has
 *  been *to* on the 4th and is still ahead. */
function commitmentBehind(commitment: Commitment, now: Date): boolean {
  const when = commitment.starts_at ?? commitment.on_date
  return !!when && isBehind(when, now)
}

/** Is there a showing still ahead, by the feed's own `next_showing`? */
function showingAhead(e: FeedRow, now: Date): boolean {
  return !!e.next_showing && !isBehind(e.next_showing, now)
}

/** Has the whole thing already happened — no showing left ahead, and the
 *  run's own end (or start, for a single date) is behind `now`. Mirrors
 *  `entries.ts`'s `alreadyHappened`, restated because that file has its own
 *  extensionless imports and cannot be pulled in here. */
function hasPassed(e: FeedRow, commitment: Commitment | null, now: Date): boolean {
  // A festival or market is on until its last day is over, whatever time a
  // showing printed (`daylong.ts`) — unless Doug has pinned a time of his own.
  if (!commitment) {
    const ended = daylongHasEnded(e, berlinDay(now))
    if (ended !== null) return ended
  }
  const next = commitment?.starts_at ?? commitment?.on_date ?? e.next_showing
  if (next) return isBehind(next, now)
  const end = e.run_end ?? e.run_start
  return !!end && isBehind(end, now)
}

/** "This week", "Next week", then the month a further-out date falls in — a
 *  rolling window off `now` rather than the calendar week, the same shape
 *  `shortlistSummary`'s own "this week" already uses. */
function headingFor(when: string, now: Date): string {
  const t = new Date(when).getTime()
  if (t < now.getTime() + MS_WEEK) return 'This week'
  if (t < now.getTime() + 2 * MS_WEEK) return 'Next week'
  const d = new Date(when)
  const month = d.toLocaleDateString('en-GB', { month: 'long', timeZone: 'UTC' })
  return d.getUTCFullYear() === now.getUTCFullYear() ? month : `${month} ${d.getUTCFullYear()}`
}

/** `FeedRow` already has its own `status` — the entry's lifecycle
 *  (`announced`/`cancelled`), a string, nothing like this one. The brief's own
 *  prose calls this field "status"; naming it that in the type would collide
 *  with the real one rather than override it — a plain intersection makes
 *  TypeScript compute the shared property as `string & ('scheduled' | 'pick'
 *  | null)`, which has no `null` (it does not intersect `string`), so every
 *  `null` this function actually produces would fail to typecheck, and
 *  spreading `{ ...e, status }` at runtime would overwrite the real
 *  lifecycle value with the calendar one on the object the sheet and the
 *  menu then read — the "one value, two jobs" bug this codebase keeps
 *  writing, caught here before it reached either. `calendarStatus` says what
 *  it is and leaves `status` alone. */
type UpcomingRow = FeedRow & { calendarStatus: 'scheduled' | 'pick' | null }

export interface ShortlistGroups {
  upcoming: { heading: string; rows: UpcomingRow[] }[]
  undated: FeedRow[]
  went: (FeedRow & { went: boolean | null })[]
}

/**
 * `saved` split three ways, for the Shortlist's own three tabs.
 *
 * - **Upcoming**: has a date ahead of `now`, grouped under a heading and
 *   marked `scheduled` (a commitment already answers "which night"), `pick`
 *   (several showings and nothing chosen yet), or `null` (one date, nothing
 *   to pick).
 * - **Undated**: no run at all and no commitment — nothing to group by.
 * - **Went**: the date has passed. `went` is always `null` here — this
 *   function has no signal to read one from (that lives in `taste_signal`,
 *   which the screen fetches on its own); it is typed on the row only so the
 *   screen has somewhere to put the tick once it knows one.
 */
export function groupShortlist(
  saved: FeedRow[],
  showings: Map<string, Showing[]>,
  commitments: Commitment[],
  now: Date,
): ShortlistGroups {
  const undated: FeedRow[] = []
  const went: (FeedRow & { went: boolean | null })[] = []
  const upcomingRows: { when: string; heading: string; row: UpcomingRow }[] = []

  for (const e of saved) {
    const pinned = commitmentFor(e, commitments)
    if (!e.run_start && !pinned) { undated.push(e); continue }
    // A pinned night that has gone by does not end an event that still has
    // one ahead. `hasPassed` read the commitment *instead of* `next_showing`,
    // so one value answered both "which night did I pick" and "is anything
    // left", and Memory of Berlin — pinned for 4 Oct 18:00, screening again on
    // 6 Oct — was Went at 19:45 on the 4th. With the stale pin set aside the
    // row is judged on its showings alone: Upcoming, and not "in the Year"
    // for a night that is over (a pick, if several remain).
    const commitment = pinned && commitmentBehind(pinned, now) && showingAhead(e, now) ? null : pinned
    if (hasPassed(e, commitment, now)) { went.push({ ...e, went: null }); continue }

    const when = whenOf(e, commitment) ?? e.run_start!
    const dated = showingsFor(e, showings).filter(s => s.kind !== 'run')
    const calendarStatus: 'scheduled' | 'pick' | null = commitment ? 'scheduled' : dated.length > 1 ? 'pick' : null
    upcomingRows.push({ when, heading: headingFor(when, now), row: { ...e, calendarStatus } })
  }

  upcomingRows.sort((a, b) => (a.when < b.when ? -1 : a.when > b.when ? 1 : 0))
  went.sort((a, b) => {
    const aw = whenOf(a, commitmentFor(a, commitments)) ?? a.run_start ?? ''
    const bw = whenOf(b, commitmentFor(b, commitments)) ?? b.run_start ?? ''
    return aw < bw ? 1 : aw > bw ? -1 : 0
  })

  const order: string[] = []
  const byHeading = new Map<string, UpcomingRow[]>()
  for (const { heading, row } of upcomingRows) {
    let bucket = byHeading.get(heading)
    if (!bucket) { bucket = []; byHeading.set(heading, bucket); order.push(heading) }
    bucket.push(row)
  }

  return {
    upcoming: order.map(heading => ({ heading, rows: byHeading.get(heading)! })),
    undated,
    went,
  }
}
